import { LMSAction } from '@nsc/simple-lms/init.js';

export enum SmartTeamsPermission {
  // Company Management
  CompanyCreate = 'CompanyCreate',
  CompanyRead = 'CompanyRead',
  CompanyUpdate = 'CompanyUpdate',
  CompanyDelete = 'CompanyDelete',
  CompanyView = 'CompanyView',
  /** Edit who the smartteams-app "Email service desk" button addresses. Granted to NSC Admin only. */
  ServiceDeskSettingsManage = 'ServiceDeskSettingsManage',

  CustomerCreate = 'CustomerCreate',
  CustomerRead = 'CustomerRead',
  CustomerUpdate = 'CustomerUpdate',
  CustomerDelete = 'CustomerDelete',
  CustomerView = 'CustomerView',

  // User Management
  UserManage = 'UserManage',
  'Users.Impersonate' = 'Users.Impersonate',
  'Users.ChangePassword' = 'Users.ChangePassword',

  // Field Engineer Management
  FECreate = 'FECreate',
  FERead = 'FERead',
  FEUpdate = 'FEUpdate',
  FEDelete = 'FEDelete',
  FEView = 'FEView',
  FEImportExportAdmin = 'FEImportExportAdmin',

  // Availability Management
  AvailabilityCreate = 'AvailabilityCreate',
  AvailabilityRead = 'AvailabilityRead',
  AvailabilityUpdate = 'AvailabilityUpdate',
  AvailabilityDelete = 'AvailabilityDelete',
  AvailabilityView = 'AvailabilityView',

  CalendarView = 'CalendarView',

  // File Management
  FileManage = 'FileManage',

  // API Access
  APIAccess = 'APIAccess',
}

// Role names
export const SmartTeamsRoleNames = {
  NSC_ADMIN: 'NSC Admin',
  PARTNER_MANAGER: 'Partner Manager',
  NSC_AGENT: 'NSC Agent',
  CUSTOMER: 'Customer',
} as const;

// Role definitions with their permissions
/**
 * Permissions that can be scoped to specific companies.
 * When a user is assigned to a company, these permissions get that company in their scope.
 */
export const COMPANY_SCOPABLE_PERMISSIONS = [
  SmartTeamsPermission.FECreate,
  SmartTeamsPermission.FERead,
  SmartTeamsPermission.FEUpdate,
  SmartTeamsPermission.FEDelete,
  SmartTeamsPermission.AvailabilityCreate,
  SmartTeamsPermission.AvailabilityRead,
  SmartTeamsPermission.AvailabilityUpdate,
  SmartTeamsPermission.AvailabilityDelete,
] as const;

export const SmartTeamsRoleDefinitions = {
  [SmartTeamsRoleNames.NSC_ADMIN]: {
    roleName: SmartTeamsRoleNames.NSC_ADMIN,
    permissions: (Object.values(SmartTeamsPermission) as string[]).concat(Object.values(LMSAction)),
  },
  [SmartTeamsRoleNames.PARTNER_MANAGER]: {
    roleName: SmartTeamsRoleNames.PARTNER_MANAGER,
    permissions: [
      SmartTeamsPermission.FECreate,
      SmartTeamsPermission.FERead,
      SmartTeamsPermission.FEUpdate,
      SmartTeamsPermission.FEDelete,
      SmartTeamsPermission.FEView,
      SmartTeamsPermission.AvailabilityCreate,
      SmartTeamsPermission.AvailabilityRead,
      SmartTeamsPermission.AvailabilityUpdate,
      SmartTeamsPermission.AvailabilityDelete,
      SmartTeamsPermission.AvailabilityView,
      SmartTeamsPermission.FileManage,
    ],
  },
  [SmartTeamsRoleNames.NSC_AGENT]: {
    roleName: SmartTeamsRoleNames.NSC_AGENT,
    permissions: [
      SmartTeamsPermission.CompanyRead,
      SmartTeamsPermission.FERead,
      SmartTeamsPermission.AvailabilityRead,
      SmartTeamsPermission.CalendarView,
    ],
  },
  // Placeholder so the role row exists after admin startup and shows up in role dropdowns.
  // The smartteams-app's ensureSmartTeamsAppRoles seeds TicketRead/View/Review via $addToSet.
  [SmartTeamsRoleNames.CUSTOMER]: {
    roleName: SmartTeamsRoleNames.CUSTOMER,
    permissions: [],
  },
} as const;

/**
 * Route to Permission mapping.
 * Defines which permission is required to access each route.
 * null = public route (no permission required)
 */
export const ROUTE_PERMISSION_MAP = {
  '/partner-companies': SmartTeamsPermission.CompanyView,
  '/customers': SmartTeamsPermission.CustomerView,
  '/roles': SmartTeamsPermission.UserManage,
  '/users': SmartTeamsPermission.UserManage,
  '/calendar': SmartTeamsPermission.CalendarView,
  '/availabilities': SmartTeamsPermission.AvailabilityView,
  '/field-engineers': SmartTeamsPermission.FEView,
  '/field-engineers-import-export': SmartTeamsPermission.FEImportExportAdmin,
  '/courses': LMSAction.ViewCourse,
};
